import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * CandidatureModal · crea o edita una candidatura (C1 Tracker).
 *
 * Reusable desde 3 sitios:
 *   1. SavedCallsTab (`/funding`)  — preset call_id, elige proyecto + versión
 *   2. ProjectEditor → tab Financiación — preset project_id, elige call
 *   3. CandidaturesList — edición de candidatura existente
 *
 * Props:
 *   - mode: "create" | "edit"
 *   - initial: { project_id?, call_id?, version_id?, amount_requested?, ... }
 *   - candidature: documento existente (modo edit)
 *   - onClose, onSaved
 */

export default function CandidatureModal({ mode = "create", initial = {}, candidature, onClose, onSaved }) {
  const isEdit = mode === "edit" && candidature;
  const [form, setForm] = useState(() => isEdit ? {
    ...candidature,
  } : {
    project_id: initial.project_id || "",
    call_id: initial.call_id || "",
    version_id: initial.version_id || "",
    amount_requested: initial.amount_requested || 0,
    currency: "EUR",
    status: "preparando",
    sent_at: "",
    resolution_expected_at: "",
    notes: "",
    link_pipeline: true,
  });
  const [projects, setProjects] = useState([]);
  const [calls, setCalls] = useState([]);
  const [versions, setVersions] = useState([]);  // del proyecto seleccionado
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Cargar proyectos y convocatorias (solo en create — en edit no se cambian)
  useEffect(() => {
    if (isEdit) return;
    api.get("/icaa/projects").then((r) => setProjects(r.data || []));
    api.get("/funding/saved").then((r) => setCalls(r.data || []));
  }, [isEdit]);

  // Cargar versiones cuando cambie el proyecto
  useEffect(() => {
    if (!form.project_id) { setVersions([]); return; }
    api.get(`/icaa/projects/${form.project_id}/versions`).then((r) => {
      const list = r.data?.versions || [];
      setVersions(list);
      // Auto-pick si solo hay una
      if (!isEdit && list.length === 1 && !form.version_id) {
        setForm((f) => ({ ...f, version_id: list[0].id }));
      } else if (!isEdit && list.length > 1 && !form.version_id && r.data?.active_version_id) {
        setForm((f) => ({ ...f, version_id: r.data.active_version_id }));
      }
    });
  }, [form.project_id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.project_id) return setError("Elige un proyecto.");
    if (!form.call_id) return setError("Elige una convocatoria.");
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/candidatures/${candidature.id}`, {
          version_id: form.version_id,
          amount_requested: Number(form.amount_requested) || 0,
          amount_granted: Number(form.amount_granted) || 0,
          status: form.status,
          sent_at: form.sent_at,
          resolution_expected_at: form.resolution_expected_at,
          resolution_at: form.resolution_at,
          notes: form.notes,
        });
      } else {
        await api.post("/candidatures", {
          project_id: form.project_id,
          call_id: form.call_id,
          version_id: form.version_id || null,
          amount_requested: Number(form.amount_requested) || 0,
          currency: form.currency,
          status: form.status,
          sent_at: form.sent_at || null,
          resolution_expected_at: form.resolution_expected_at || null,
          notes: form.notes,
          link_pipeline: form.link_pipeline,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Error al guardar.");
    } finally {
      setSubmitting(false);
    }
  };

  const callTitle = useMemo(() => {
    if (isEdit) return candidature.call_title;
    const c = calls.find((x) => x.id === form.call_id);
    return c?.title || "—";
  }, [calls, form.call_id, isEdit, candidature]);

  const projectTitle = useMemo(() => {
    if (isEdit) return candidature.project_title;
    const p = projects.find((x) => x.id === form.project_id);
    return p?.title || "—";
  }, [projects, form.project_id, isEdit, candidature]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gb-black/70 p-4 backdrop-blur"
         onClick={onClose} data-testid="candidature-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
        <p className="gb-eyebrow">Candidaturas</p>
        <h2 className="mt-1 gb-display text-2xl">
          {isEdit ? "Editar candidatura" : "Presentar a una convocatoria"}
        </h2>
        <p className="mt-2 text-sm text-gb-muted">
          {isEdit
            ? `${projectTitle} → ${callTitle}`
            : "Vincula un proyecto + versión del dossier con una convocatoria abierta."}
        </p>

        <div className="mt-5 space-y-4">
          {/* Proyecto */}
          {!isEdit && (
            <Field label="Proyecto">
              {initial.project_id ? (
                <p className="mt-1 rounded-md bg-gb-paper-soft px-3 py-2 text-sm" data-testid="cand-project-fixed">
                  {projectTitle}
                </p>
              ) : (
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, version_id: "" })}
                        required data-testid="cand-project"
                        className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
                  <option value="">— elige proyecto —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              )}
            </Field>
          )}

          {/* Convocatoria */}
          {!isEdit && (
            <Field label="Convocatoria">
              {initial.call_id ? (
                <p className="mt-1 rounded-md bg-gb-paper-soft px-3 py-2 text-sm" data-testid="cand-call-fixed">
                  {callTitle}
                </p>
              ) : (
                <select value={form.call_id} onChange={(e) => setForm({ ...form, call_id: e.target.value })}
                        required data-testid="cand-call"
                        className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
                  <option value="">— elige convocatoria —</option>
                  {calls.map((c) => <option key={c.id} value={c.id}>{c.title} {c.closes_at && `(cierra ${c.closes_at})`}</option>)}
                </select>
              )}
            </Field>
          )}

          {/* Versión del dossier (si hay más de una) */}
          {!isEdit && versions.length > 1 && (
            <Field label="Versión del dossier">
              <select value={form.version_id} onChange={(e) => setForm({ ...form, version_id: e.target.value })}
                      data-testid="cand-version"
                      className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
                {versions.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-gb-muted">Hay {versions.length} versiones — elige cuál se presenta.</p>
            </Field>
          )}
          {!isEdit && versions.length === 1 && (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
              ✓ Versión auto-seleccionada: <strong>{versions[0].name}</strong> (única disponible).
            </p>
          )}

          {/* Importe + estado */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Importe solicitado (€)">
              <input type="number" min="0" step="100" value={form.amount_requested}
                     onChange={(e) => setForm({ ...form, amount_requested: e.target.value })}
                     data-testid="cand-amount-req"
                     className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
            </Field>
            <Field label="Estado">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="cand-status"
                      className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red">
                <option value="preparando">Preparando</option>
                <option value="enviada">Enviada</option>
                <option value="esperando">Esperando resolución</option>
                <option value="aprobada">Aprobada</option>
                <option value="denegada">Denegada</option>
              </select>
            </Field>
          </div>

          {/* Importe concedido (solo si aprobada) */}
          {form.status === "aprobada" && (
            <Field label="Importe concedido (€)">
              <input type="number" min="0" step="100" value={form.amount_granted || 0}
                     onChange={(e) => setForm({ ...form, amount_granted: e.target.value })}
                     data-testid="cand-amount-granted"
                     className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
            </Field>
          )}

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha de envío (opcional)">
              <input type="date" value={(form.sent_at || "").slice(0, 10)}
                     onChange={(e) => setForm({ ...form, sent_at: e.target.value })}
                     data-testid="cand-sent-at"
                     className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
            </Field>
            <Field label="Resolución prevista (opcional)">
              <input type="date" value={(form.resolution_expected_at || "").slice(0, 10)}
                     onChange={(e) => setForm({ ...form, resolution_expected_at: e.target.value })}
                     data-testid="cand-res-expected"
                     className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-sm outline-none focus:border-gb-red" />
            </Field>
          </div>

          {/* Vincular con Pipeline */}
          {!isEdit && (
            <label className="flex items-start gap-2 rounded-md bg-gb-paper-soft p-3 text-sm">
              <input type="checkbox" checked={form.link_pipeline}
                     onChange={(e) => setForm({ ...form, link_pipeline: e.target.checked })}
                     data-testid="cand-link-pipeline"
                     className="mt-1" />
              <span>
                <strong className="block">Vincular con el Pipeline del proyecto</strong>
                <span className="text-gb-muted">
                  Si la organización de esta convocatoria está en tu pipeline, mueve la tarjeta a "Enviado dossier" automáticamente.
                </span>
              </span>
            </label>
          )}

          <Field label="Notas internas (opcional)">
            <textarea rows={3} value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="cand-notes"
                      placeholder="Nº expediente, persona contacto, condiciones…"
                      className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2 text-sm outline-none focus:border-gb-red" />
          </Field>

          {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="cand-error">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-full border border-gb-paper px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-muted hover:bg-gb-paper-soft">
            Cancelar
          </button>
          <button type="submit" disabled={submitting} data-testid="cand-submit"
                  className="rounded-full bg-gb-red px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red disabled:opacity-40">
            {submitting ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear candidatura"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gb-muted">{label}</span>
    {children}
  </label>
);
